import type {
  CmmdRuntimeInventorySnapshot,
  CmmdThreadDisposition,
  CmmdThreadDispositionAction,
  ProjectId,
  ScopedThreadRef,
} from "@cmmd-forge/contracts";

import {
  cmmdRuntimeInventoryResource,
  findCmmdRuntimeInventoryThread,
} from "../cmmd/cmmdRuntimeInventoryResource";
import { resolveRuntimeIdForEnvironment } from "../cmmd/runtimeAvailabilityForEnvironment";
import { CmmdApiError, createCmmdApiClient } from "../environments/cmmdApi";
import { newCommandId } from "./utils";

function readRevision(
  inventory: CmmdRuntimeInventorySnapshot | null,
  runtimeId: string,
  projectId: string,
  threadId: string,
): number {
  return (
    findCmmdRuntimeInventoryThread(inventory, runtimeId, projectId, threadId)?.disposition
      .revision ?? 0
  );
}

async function writeDisposition(
  target: ScopedThreadRef,
  projectId: ProjectId,
  action: CmmdThreadDispositionAction,
  expectedRevision: number,
): Promise<CmmdThreadDisposition> {
  return createCmmdApiClient().putThreadDisposition(
    resolveRuntimeIdForEnvironment(target.environmentId),
    projectId,
    target.threadId,
    { action, idempotencyKey: newCommandId(), expectedRevision },
  );
}

/** Write the user's filing choice to CMMD, then refresh the shared projection. */
export async function resolveThreadLifecycleDispatch(
  target: ScopedThreadRef,
  projectId: ProjectId,
  action: CmmdThreadDispositionAction,
): Promise<CmmdThreadDisposition> {
  const runtimeId = resolveRuntimeIdForEnvironment(target.environmentId);
  const current = cmmdRuntimeInventoryResource.read().inventory;
  const revision = readRevision(current, runtimeId, projectId, target.threadId);

  try {
    const result = await writeDisposition(target, projectId, action, revision);
    await cmmdRuntimeInventoryResource.refresh({ allWorkspaces: true });
    return result;
  } catch (error) {
    if (!(error instanceof CmmdApiError) || error.status !== 409) throw error;

    const refreshed = await cmmdRuntimeInventoryResource.refresh({ allWorkspaces: true });
    const result = await writeDisposition(
      target,
      projectId,
      action,
      readRevision(refreshed, runtimeId, projectId, target.threadId),
    );
    await cmmdRuntimeInventoryResource.refresh({ allWorkspaces: true });
    return result;
  }
}
